import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useSEO } from '../hooks/useSEO';
import { trackEvent } from '../utils/facebookPixel';
import { clearPlayerNextPath, readPlayerNextPath, rememberPlayerNextPath } from '../utils/playerNextPath';
import toast from 'react-hot-toast';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import PasswordInput from '../components/PasswordInput';

const LoginContainer = styled.div`
  max-width: 400px;
  margin: 2rem auto;
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
`;

const LoginHeader = styled.div`
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--primary-blue-dark) 50%, var(--brand-red) 100%);
  color: var(--white);
  padding: 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(213, 43, 30, 0.2) 0%, transparent 50%, rgba(4, 54, 99, 0.2) 100%);
    pointer-events: none;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  opacity: 0.9;
  margin: 0;
`;

const FormContainer = styled.div`
  padding: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid var(--border-color);
  border-radius: var(--radius-md);
  font-size: 1rem;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-blue);
    box-shadow: 0 0 0 3px rgb(0 78 158 / 0.1);
  }
  
  &.error {
    border-color: var(--error);
  }
`;

const ErrorMessage = styled.span`
  color: var(--error);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
`;

const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #043663 0%, #0a4d7a 100%);
  color: #ffffff;
  border: 2px solid #043663;
  padding: 1rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 1rem;
  box-shadow: 0 4px 6px -1px rgba(4, 54, 99, 0.2);
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #032a4a 0%, #043663 100%);
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(4, 54, 99, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    background: #6b7280;
    border-color: #6b7280;
  }
`;

const InstagramPromoCard = styled.div`
  margin: 0 1.75rem 2.5rem;
  padding: 1.75rem 1.5rem;
  background: radial-gradient(circle at top left, rgba(213, 43, 30, 0.12), transparent 55%),
    radial-gradient(circle at bottom right, rgba(4, 54, 99, 0.12), transparent 50%),
    #ffffff;
  border: 1px solid rgba(4, 54, 99, 0.12);
  border-radius: 18px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 12px 32px -20px rgba(4, 54, 99, 0.45);

  @media (max-width: 768px) {
    margin: 0 1.25rem 2rem;
    padding: 1.5rem 1.25rem;
  }
`;

const InstagramPromoHeading = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  color: #d52b1e;
  font-size: 1rem;

  span {
    font-size: 1.25rem;
  }
`;

const InstagramPromoText = styled.p`
  margin: 0;
  color: var(--text-primary);
  font-size: 0.95rem;
  line-height: 1.6;
  max-width: 320px;
`;

const InstagramPromoButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.85rem 1.75rem;
  background: linear-gradient(135deg, #d52b1e 0%, #043663 100%);
  color: #ffffff;
  border-radius: 999px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  box-shadow: 0 8px 16px -6px rgba(4, 54, 99, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-width: 220px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px -8px rgba(4, 54, 99, 0.45);
  }

  &:focus-visible {
    outline: 3px solid rgba(4, 54, 99, 0.3);
    outline-offset: 3px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const RegisterLink = styled.div`
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const LinkStyled = styled(Link)`
  color: var(--primary-blue);
  text-decoration: none;
  font-weight: 600;
  
  &:hover {
    text-decoration: underline;
  }
`;

const HelpText = styled.small`
  color: var(--text-secondary);
  font-size: 0.75rem;
  margin-top: 0.25rem;
  display: block;
`;

const Phase2Notice = styled.div`
  background: #fff8e6;
  border: 1px solid #f0d48a;
  border-radius: var(--radius-md);
  padding: 0.85rem 1rem;
  margin-bottom: 1.25rem;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #5c4a12;
`;

const PlayerLoginHelpBanner = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: var(--radius-md);
  padding: 0.9rem 1rem;
  margin-bottom: 1.25rem;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #1e3a5f;
  text-align: center;
`;

const PlayerLoginHelpLink = styled(Link)`
  display: inline-block;
  margin-top: 0.35rem;
  color: var(--primary-blue);
  font-weight: 700;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ForgotPasswordLink = styled.button`
  background: none;
  border: none;
  color: var(--primary-blue);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0;
  margin-top: 0.5rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SuccessMessage = styled.div`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  text-align: center;
  font-weight: 500;
`;

const LoginPage = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const teamLoginSunsetDate =
    process.env.REACT_APP_PHASE2_TEAM_LOGIN_SUNSET || '20 July 2026';
  const playerLoginOnly =
    String(process.env.REACT_APP_PHASE2_DISABLE_TEAM_LOGIN || '').toLowerCase() === 'true';

  // Debug modal state changes
  useEffect(() => {
    console.log('🔒 LoginPage: showForgotPassword state changed to:', showForgotPassword);
  }, [showForgotPassword]);
  
  // SEO Meta Tags
  useSEO({
    title: 'Team Login - Access Your Padel Dashboard',
    description: 'Login to your Helvetic Padel Series team dashboard. Access your match schedule, team members, standings, and league information. Secure team login for registered padel teams.',
    keywords: 'padel login, team login, padel dashboard, helvetic padel login, padel team access, padel league login, Switzerland padel login, padel team dashboard',
    canonical: 'https://helveticpadelseries.ch/login'
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Track login page view
  useEffect(() => {
    trackEvent('ViewContent', {
      content_name: 'Login Page',
      content_category: 'Authentication'
    });
  }, []);

  const [successMessage, setSuccessMessage] = useState('');

  const {
    register,
    control,
    handleSubmit,
    formState: { errors }
  } = useForm();

  useEffect(() => {
    rememberPlayerNextPath(searchParams.get('next'));
  }, [searchParams]);

  // Handle success message from URL parameters
  useEffect(() => {
    const message = searchParams.get('message');
    const registered = searchParams.get('registered');
    const nextKeep = searchParams.get('next')
      ? `?next=${encodeURIComponent(searchParams.get('next'))}`
      : '';
    if (message) {
      setSuccessMessage(message);
      navigate(`/login${nextKeep}`, { replace: true });
    } else if (registered === '1') {
      setSuccessMessage('Account created. Please log in with your email and password.');
      navigate(`/login${nextKeep}`, { replace: true });
    }
  }, [searchParams, navigate]);

  const onSubmit = async (data) => {
    // Don't submit login if forgot password modal is open
    if (showForgotPassword) {
      return;
    }
    
    setLoading(true);
    
    console.log('🔍 Login attempt started on mobile:', navigator.userAgent.includes('Mobile'));
    console.log('📱 Login data:', data);
    
    try {
      const result = await login(data);
      
      console.log('📊 Login result:', result);
      
      if (result.success) {
        console.log('✅ Login successful, navigating...');

        trackEvent('Login', {
          content_name: 'Login',
          content_category: 'Authentication'
        });

        const nextPath = readPlayerNextPath(searchParams.get('next'));
        if (nextPath) clearPlayerNextPath();
        let target =
          (result.accountType === 'player' && nextPath) ||
          result.redirectUrl ||
          (result.accountType === 'player' ? '/player' : '/dashboard');
        if (
          result.accountType === 'player' &&
          result.profileComplete === false &&
          typeof target === 'string'
        ) {
          const generic =
            target === '/player' ||
            target === '/player/' ||
            /^\/player\/?(\?|$)/.test(target);
          if (generic) {
            target = '/player/settings/profile';
          }
        }
        console.log('🔄 Redirecting to:', target);
        if (result.accountType === 'player') {
          if (result.profileComplete === false) {
            toast(t('login.completeProfilePrompt'), { duration: 5500 });
          } else {
            toast.success(t('login.loginSuccess'));
          }
        }
        navigate(target);
      } else {
        console.log('❌ Login failed:', result.message);
      }
    } catch (error) {
      console.error('💥 Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginHeader>
        <Title>{t('login.title')}</Title>
        <Subtitle>{t('login.subtitle')}</Subtitle>
      </LoginHeader>

      <FormContainer>
        {successMessage && (
          <SuccessMessage>
            {successMessage}
          </SuccessMessage>
        )}
        <PlayerLoginHelpBanner>
          {t('playerLoginHelp.loginBanner')}{' '}
          <PlayerLoginHelpLink to="/player-login-help">
            {t('playerLoginHelp.loginBannerLink')}
          </PlayerLoginHelpLink>
        </PlayerLoginHelpBanner>
        {!playerLoginOnly && (
          <Phase2Notice>{t('login.phase2Notice', { sunsetDate: teamLoginSunsetDate })}</Phase2Notice>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label htmlFor="login">
              {playerLoginOnly ? t('login.playerEmail') : t('login.emailOrTeamName')} *
            </Label>
            <Input
              id="login"
              type={playerLoginOnly ? 'email' : 'text'}
              {...register('login', {
                required: playerLoginOnly
                  ? t('login.playerEmailRequired')
                  : t('login.emailOrTeamNameRequired'),
              })}
              className={errors.login ? 'error' : ''}
              placeholder={
                playerLoginOnly
                  ? t('login.playerEmailPlaceholder')
                  : t('login.emailOrTeamNamePlaceholder')
              }
            />
            {errors.login && <ErrorMessage>{errors.login.message}</ErrorMessage>}
            <HelpText>
              {playerLoginOnly
                ? t('login.playerEmailHelp')
                : t('login.emailOrTeamNameHelp', { sunsetDate: teamLoginSunsetDate })}
            </HelpText>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">{t('login.password')} *</Label>
            <Controller
              name="password"
              control={control}
              rules={{ required: t('login.passwordRequired') }}
              render={({ field }) => (
                <PasswordInput
                  id="password"
                  {...field}
                  className={errors.password ? 'error' : ''}
                  placeholder={t('login.passwordPlaceholder')}
                />
              )}
            />
            {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
            <ForgotPasswordLink 
              type="button"
              onClick={() => {
                console.log('🔒 LoginPage: Opening forgot password modal');
                setShowForgotPassword(true);
              }}
            >
              Forgot your password?
            </ForgotPasswordLink>
          </FormGroup>

          <SubmitButton type="submit" disabled={loading}>
            {loading ? t('login.signingIn') : t('login.signIn')}
          </SubmitButton>
        </form>

        <RegisterLink>
          {t('login.noTeamYet')} <LinkStyled to="/register">{t('login.registerTeam')}</LinkStyled>
          {' · '}
          <LinkStyled to="/register-player">Sign up as player</LinkStyled>
        </RegisterLink>
      </FormContainer>

      <InstagramPromoCard>
        <InstagramPromoHeading>
          <span>📸</span>
          Follow Helvetic Padel
        </InstagramPromoHeading>
        <InstagramPromoText>
          Want to see all the updates, highlights, and videos from the Helvetic Padel Series? Follow our Instagram account to stay in the loop!
        </InstagramPromoText>
        <InstagramPromoButton
          href="https://www.instagram.com/helveticpadel/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Follow on Instagram
        </InstagramPromoButton>
      </InstagramPromoCard>

      <ForgotPasswordModal 
        isOpen={showForgotPassword} 
        onClose={() => setShowForgotPassword(false)} 
      />
    </LoginContainer>
  );
};

export default LoginPage;
